import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';
import type { SearchProps } from 'webapp/utils/filter';
import { isVerified } from 'webapp/utils/verification';

import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';

export const enum ViewType {
	BOARD = 'databoard',
	ACCOUNT_TEMPLATE = 'template',
	LOOP = 'loop',
}

// almost like board filter-sort but for mixed content (boards, templates)
export default class FilterSortMixedInDesigner extends FilterSort<Board | BoardLoop> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('billing') declare billingService: Service.Billing;

	@tracked override sortProp = 'order';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.ASC;

	override searchProps: SearchProps = {
		name: 1,
	};

	override settingsNamePrefix = 'databoardSelectListFilterSettings';
	override viewSelectorStyle = 'text';

	constructor(options: FilterSortSettings<Board | BoardLoop>, viewId: string) {
		super(options);

		this.viewId = viewId;

		const selectedFilters = this.readSavedSelectedFilters();

		// Tags (used only)
		this.createFilter<number>({
			id: 2,
			label: 'Tags',
			name: 'tag',
			selected: selectedFilters[2] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.tagOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedValue) => item.tags.includes(selectedValue));
			},
		});

		// Data source (used only)
		this.createFilter<string>({
			id: 3,
			label: 'Data source',
			name: 'data source',
			selected: selectedFilters[3] as string | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.datasourceOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((selectedValue) => {
					if (this.viewId === 'template') {
						return !!(item as Board).templateModel?.specificSourceTypesKeys.includes(
							selectedValue,
						);
					} else if (this.viewId === 'loop') {
						return true; // filter not visible for loops
					} else {
						const sourceTypeId =
							this.mmSourceTypesService.findModelByKey(selectedValue)?.id;
						return (
							item.specificSourceTypesKeys.includes(selectedValue) ||
							!!(
								sourceTypeId &&
								(
									item as Board
								).sourceTypeIdsMissingFromResolutionMessages?.includes(sourceTypeId)
							)
						);
					}
				});
			},
		});

		// Creator
		this.createFilter<number>({
			id: 4,
			label: 'Created by',
			name: 'creator',
			selected: selectedFilters[4] as number | undefined,
			getOptions: () => {
				return this.creatorOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedValue) => {
					if (selectedValue === -1) {
						// exception: all others
						return !item.isMine;
					} else {
						return item.createdById === selectedValue;
					}
				});
			},
		});

		// Category
		this.createFilter<number>({
			id: 1,
			label: 'Show',
			name: 'category',
			selected: selectedFilters[1] as number | undefined,
			getOptions: () => {
				return this.categoryOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedValue) => {
					switch (selectedValue) {
						case 5: // favorite
							return item.favorite;
						case 6: // verified
							return 'verificationInfo' in item && isVerified(item.verificationInfo);
						default:
							return false;
					}
				});
			},
		});
	}

	// get isLoopView(): boolean {
	// 	return this.viewId === 'loop';
	// }

	// get isTemplateView(): boolean {
	// 	return this.viewId === 'template';
	// }

	// get isDataboardView(): boolean {
	// 	return this.viewId === 'databoard';
	// }

	override get hideViewSelector(): boolean {
		// to prevent hiding view selectors if any of view have zero items
		return false;
	}

	override get viewOptions(): FilterOption<ViewType>[] {
		const optionsArray = [{ id: ViewType.BOARD, text: 'Dashboards' }];

		if (this.billingService.boardLoopsEnabled) {
			optionsArray.push({ id: ViewType.LOOP, text: 'Looped dashboards' });
		}

		if (this.billingService.accountTemplatesEnabled) {
			optionsArray.push({ id: ViewType.ACCOUNT_TEMPLATE, text: 'Account templates' });
		}

		return optionsArray;
	}

	get categoryOptions(): FilterOption<number>[] {
		// id of filters shouldn't be changed (only add or remove) because they are saved in usres local storage
		// id [1,2,3,4] have already been used, but later removed so they shouldn't be used again
		return [
			{ id: 0, text: 'All' },
			{ id: 5, text: 'Favorites', icon: 'StarFill', color: 'var(--databox-yellow-500)' },
		];
	}

	get tagOptions(): FilterOption<number>[] {
		// when switching viewId in databoard-select-list component, we get viewId
		// assignedTagsForBoards is used for boards and assignedTagsForAccountTemplates is used for templates.
		if (this.viewId === ViewType.ACCOUNT_TEMPLATE) {
			return this.tagOptionsForTemplates;
		} else if (this.viewId === ViewType.LOOP) {
			return this.tagOptionsForLoops;
		} else {
			return this.tagOptionsForBoards;
		}
	}

	@cached
	get tagOptionsForTemplates(): FilterOption<number>[] {
		const values = this.mmBoardsService.assignedTagsForAccountTemplates,
			arrayCopy = Array.from(values) as FilterOption<number>[];
		if (arrayCopy.length) {
			arrayCopy.unshift({ id: 0, text: 'All' });
		}
		return arrayCopy;
	}

	@cached
	get tagOptionsForBoards(): FilterOption<number>[] {
		const values = this.mmBoardsService.assignedTagsForBoards,
			arrayCopy = Array.from(values) as FilterOption<number>[];
		if (arrayCopy.length) {
			arrayCopy.unshift({ id: 0, text: 'All' });
		}
		return arrayCopy;
	}

	@cached
	get tagOptionsForLoops(): FilterOption<number>[] {
		const values = this.mmBoardLoopsService.assignedTags,
			arrayCopy = Array.from(values) as FilterOption<number>[];
		if (arrayCopy.length) {
			arrayCopy.unshift({ id: 0, text: 'All' });
		}
		return arrayCopy;
	}

	get datasourceOptions(): FilterOption<string>[] {
		if (this.viewId === ViewType.ACCOUNT_TEMPLATE) {
			return this.datasourceOptionsForTemplates;
		} else if (this.viewId === ViewType.LOOP) {
			return []; // filter not visible for loops
		} else {
			return this.datasourceOptionsForBoards;
		}
	}

	@cached
	get datasourceOptionsForBoards(): FilterOption<string>[] {
		// get how many boards each source is used on
		const sourceTypeArray: string[] = [];

		this.allItems.forEach((board) => {
			const sourcesFromBoard: string[] = [];
			board.specificSourceTypesKeys.forEach((key: string) => {
				if (key) {
					sourcesFromBoard.push(key);
				}
			});
			(board as Board).sourceTypeIdsMissingFromResolutionMessages.forEach(
				(missingType: number) => {
					if (missingType) {
						const missingSource = this.mmSourceTypesService.findModelById(missingType);

						if (missingSource) {
							sourcesFromBoard.push(missingSource.key);
						}
					}
				},
			);
			uniq(sourcesFromBoard).forEach((key: string) => {
				sourceTypeArray.push(key);
			});
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(sourceTypeArray);

		list.unshift({ id: 'all', text: 'All' });

		return list;
	}

	@cached
	get datasourceOptionsForTemplates(): FilterOption<string>[] {
		// get how many boards each source is used on
		const sourceTypeArray: string[] = [];

		this.allItems.forEach((board) => {
			(board.templateModel?.specificSourceTypesKeys || []).forEach((key: string) => {
				sourceTypeArray.push(key);
			});
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(sourceTypeArray);

		list.unshift({ id: 'all', text: 'All' });

		return list;
	}

	@cached
	get creatorOptions(): FilterOption<number>[] {
		const allItemCountByCreator: Record<number, number> = {};
		this.allItems.forEach((item) => {
			const createdById = item.createdById;
			if (!allItemCountByCreator[createdById]) {
				allItemCountByCreator[createdById] = 0;
			}
			allItemCountByCreator[createdById]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByCreator);
	}
}
